import React, { useState, useEffect } from 'react';
import { Search, Plus, Star, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdminTemplates, adminUpdateTemplate, adminDemoteTemplate } from '../../hooks/useAdminTemplates';
import type { AdminTemplate } from '../../hooks/useAdminTemplates';
import { PromoteTemplateModal } from './PromoteTemplateModal';

const PER_PAGE = 25;

export const AdminTemplates: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [demotingId, setDemotingId] = useState<string | null>(null);

  // Inline editing state
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryValue, setEditingCategoryValue] = useState('');
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editingOrderValue, setEditingOrderValue] = useState('');

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, categoryFilter]);

  const { templates, total, loading, refresh } = useAdminTemplates({
    category: categoryFilter,
    search: debouncedSearch,
    page,
  });

  const totalPages = Math.ceil(total / PER_PAGE);

  // Get distinct categories from current templates
  const categories = [...new Set(templates.map(t => t.template_category).filter(Boolean))] as string[];

  const handleToggleFeatured = async (template: AdminTemplate) => {
    await adminUpdateTemplate(template.id, { featured: !template.is_featured });
    refresh();
  };

  const handleCategorySave = async (id: string) => {
    await adminUpdateTemplate(id, { category: editingCategoryValue });
    setEditingCategoryId(null);
    refresh();
  };

  const handleOrderSave = async (id: string) => {
    const order = parseInt(editingOrderValue);
    if (!isNaN(order)) {
      await adminUpdateTemplate(id, { order });
    }
    setEditingOrderId(null);
    refresh();
  };

  const handleDemote = async (id: string) => {
    setDemotingId(id);
    await adminDemoteTemplate(id);
    setDemotingId(null);
    refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Templates</h1>
        <button
          onClick={() => setShowPromoteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Adicionar Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            type="text"
            placeholder="Buscar por título..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-neutral-900 border border-white/10 rounded-lg text-sm text-white placeholder-neutral-500"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto whitespace-nowrap">
          <button
            onClick={() => setCategoryFilter(null)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              categoryFilter === null ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'
            }`}
          >
            Todas
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                categoryFilter === cat ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div tabIndex={0} className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-neutral-400 text-left">
              <th className="p-4">Título</th>
              <th className="p-4">Categoria</th>
              <th className="p-4">Criador</th>
              <th className="p-4">Campos</th>
              <th className="p-4 text-center">Destaque</th>
              <th className="p-4">Ordem</th>
              <th className="p-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="p-8 text-center">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : templates.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-neutral-500">
                  Nenhum template encontrado
                </td>
              </tr>
            ) : (
              templates.map(template => (
                <tr key={template.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-4">
                    <p className="text-white">{template.title}</p>
                    {template.template_description && (
                      <p className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{template.template_description}</p>
                    )}
                  </td>
                  <td className="p-4">
                    {editingCategoryId === template.id ? (
                      <input
                        type="text"
                        value={editingCategoryValue}
                        onChange={e => setEditingCategoryValue(e.target.value)}
                        onBlur={() => handleCategorySave(template.id)}
                        onKeyDown={e => e.key === 'Enter' && handleCategorySave(template.id)}
                        autoFocus
                        className="bg-neutral-800 border border-white/10 rounded px-2 py-1 text-xs text-white w-24"
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setEditingCategoryId(template.id);
                          setEditingCategoryValue(template.template_category || '');
                        }}
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/5 text-neutral-300 hover:bg-white/10"
                      >
                        {template.template_category || '—'}
                      </button>
                    )}
                  </td>
                  <td className="p-4 text-neutral-400 text-xs">
                    {template.creator_name || template.creator_email}
                  </td>
                  <td className="p-4 text-neutral-400">{template.fields_count}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => handleToggleFeatured(template)}>
                      <Star className={`w-4 h-4 ${template.is_featured ? 'text-yellow-400 fill-yellow-400' : 'text-neutral-600'}`} />
                    </button>
                  </td>
                  <td className="p-4">
                    {editingOrderId === template.id ? (
                      <input
                        type="number"
                        value={editingOrderValue}
                        onChange={e => setEditingOrderValue(e.target.value)}
                        onBlur={() => handleOrderSave(template.id)}
                        onKeyDown={e => e.key === 'Enter' && handleOrderSave(template.id)}
                        autoFocus
                        className="bg-neutral-800 border border-white/10 rounded px-2 py-1 text-xs text-white w-16"
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setEditingOrderId(template.id);
                          setEditingOrderValue(String(template.template_order));
                        }}
                        className="text-neutral-400 hover:text-white"
                      >
                        {template.template_order}
                      </button>
                    )}
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => {
                        if (confirm('Remover template? O formulário voltará a ser um form normal.')) {
                          handleDemote(template.id);
                        }
                      }}
                      disabled={demotingId === template.id}
                      className="text-neutral-500 hover:text-red-400 transition-colors disabled:opacity-50"
                      title="Remover template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-neutral-400">
          <span>
            Mostrando {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} de {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Promote Modal */}
      {showPromoteModal && (
        <PromoteTemplateModal
          onClose={() => setShowPromoteModal(false)}
          onPromoted={refresh}
        />
      )}
    </div>
  );
};
